import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Callout,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  IconInput,
  Input,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { applyFieldErrors } from '@/shared/lib/formErrors';
import { countryCodes, countryName } from '@/shared/lib/countries';
import {
  optionalNumeric,
  toDateString,
  toMoney,
  toNumber,
  todayIso,
} from '@/shared/lib/formSchema';
import {
  CreatePlayerDtoPreferredFoot,
  CreatePlayerDtoRepresentation,
  type CreatePlayerDto,
  type CreatePlayerDtoPosition,
  type PlayerDto,
} from '@/api/generated/model';
import { useCreatePlayerMutation } from '../hooks/usePlayers';
import { PLAYER_POSITIONS, PLAYER_REPRESENTATIONS } from '../lib/playersSearch';
import { ClubSelect } from './ClubSelect';
import { PlayerPhotoUpload } from './PlayerPhotoUpload';

/** "Not specified" for the optional preferred-foot select. */
const NONE = '__none__';

export interface AddPlayerModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fills the name — onboarding's "onboard someone new" hands over what was typed. */
  initialName?: string;
  /** Called with the created player, before the dialog closes. */
  onCreated?: (player: PlayerDto) => void;
}

/**
 * Creates a player owned by the agency.
 *
 * Unlike the add-club modal this is a real create, not an assignment — there is
 * no catalog of people to pick from, so every field is typed in. The one lookup
 * is the club, which *is* catalog data.
 *
 * **There is no league field.** A league is a property of the club, and
 * offering it separately would let the two disagree; the list reads it back
 * through `club.league`.
 */
export function AddPlayerModal({ open, onClose, initialName, onCreated }: AddPlayerModalProps) {
  const { t } = useTranslation();
  const [banner, setBanner] = useState<string | null>(null);
  const createPlayer = useCreatePlayerMutation();

  const amount = optionalNumeric({ min: 0, message: t('players.form.amountInvalid') });

  const schema = z.object({
    photoKey: z.string().optional(),
    fullName: z.string().trim().min(1, t('players.form.fullNameRequired')),
    position: z.string().min(1, t('players.form.positionRequired')),
    secondaryPositions: z.array(z.string()).max(4),
    dateOfBirth: z.string().min(1, t('players.form.dateOfBirthRequired')),
    nationality: z.string().min(1, t('players.form.nationalityRequired')),
    heightCm: optionalNumeric({
      min: 120,
      max: 230,
      integer: true,
      message: t('players.form.heightInvalid'),
    }),
    preferredFoot: z.string(),
    clubId: z.string().optional(),
    marketValue: amount,
    weeklyWage: amount,
    contractUntil: z.string(),
    representation: z.string(),
  });
  type AddPlayerFormValues = z.infer<typeof schema>;

  const form = useForm<AddPlayerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: '',
      position: '',
      secondaryPositions: [],
      dateOfBirth: '',
      nationality: '',
      heightCm: '',
      preferredFoot: NONE,
      marketValue: '',
      weeklyWage: '',
      contractUntil: '',
      representation: CreatePlayerDtoRepresentation.prospect,
    },
  });

  useEffect(() => {
    if (open && initialName) form.setValue('fullName', initialName);
  }, [open, initialName, form]);

  const handleClose = () => {
    form.reset();
    createPlayer.reset();
    setBanner(null);
    onClose();
  };

  const onSubmit = (values: AddPlayerFormValues) => {
    setBanner(null);

    const heightCm = toNumber(values.heightCm);
    const marketValue = toMoney(values.marketValue);
    const weeklyWage = toMoney(values.weeklyWage);
    const contractUntil = toDateString(values.contractUntil);

    const dto: CreatePlayerDto = {
      fullName: values.fullName.trim(),
      position: values.position as CreatePlayerDtoPosition,
      secondaryPositions: values.secondaryPositions as CreatePlayerDtoPosition[],
      // A date, not a timestamp: the column is a DATE and sending an ISO
      // instant would shift the birthday by a day for anyone west of UTC.
      dateOfBirth: values.dateOfBirth,
      nationality: values.nationality,
      ...(heightCm !== undefined ? { heightCm } : {}),
      ...(values.preferredFoot !== NONE
        ? { preferredFoot: values.preferredFoot as CreatePlayerDtoPreferredFoot }
        : {}),
      ...(values.clubId ? { clubId: values.clubId } : {}),
      ...(marketValue !== undefined ? { marketValue } : {}),
      ...(weeklyWage !== undefined ? { weeklyWage } : {}),
      ...(contractUntil ? { contractUntil } : {}),
      representation: values.representation as CreatePlayerDtoRepresentation,
      ...(values.photoKey ? { photoKey: values.photoKey } : {}),
    };

    createPlayer.mutate(dto, {
      onSuccess: (player) => {
        toast.success(t('players.add.created', { name: player.fullName }));
        onCreated?.(player);
        handleClose();
      },
      onError: (error: unknown) => {
        setBanner(
          applyFieldErrors(form, error, [
            'fullName',
            'position',
            'secondaryPositions',
            'dateOfBirth',
            'nationality',
            'heightCm',
            'preferredFoot',
            'clubId',
            'marketValue',
            'weeklyWage',
            'contractUntil',
            'representation',
          ]),
        );
      },
    });
  };

  const positionOptions = PLAYER_POSITIONS.map((value) => ({
    value,
    label: `${value} · ${t(`players.position.${value}`)}`,
    short: value,
  }));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('players.add.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('players.add.title')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form noValidate className="grid gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="photoKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('players.form.photo')}</FormLabel>
                  <PlayerPhotoUpload value={field.value} onChange={field.onChange} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('players.form.fullName')}</FormLabel>
                  <FormControl>
                    <Input placeholder="Henrik Solberg" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.position')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('players.form.positionPlaceholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {positionOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="secondaryPositions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.secondaryPositions')}</FormLabel>
                    <FormControl>
                      <MultiSelect
                        value={field.value}
                        onChange={field.onChange}
                        max={4}
                        searchable={false}
                        placeholder={t('players.form.secondaryPositionsPlaceholder')}
                        options={positionOptions}
                      />
                    </FormControl>
                    <FormDescription>{t('players.form.secondaryPositionsHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="dateOfBirth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.dateOfBirth')}</FormLabel>
                    <FormControl>
                      {/* Nobody has a birthday in the future. */}
                      <Input type="date" max={todayIso()} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nationality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.nationality')}</FormLabel>
                    <FormControl>
                      <Combobox
                        value={field.value || undefined}
                        onChange={(value) => field.onChange(value ?? '')}
                        placeholder={t('players.form.nationalityPlaceholder')}
                        searchPlaceholder={t('players.form.search')}
                        options={countryCodes().map((code) => ({
                          value: code,
                          label: countryName(code),
                        }))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="heightCm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.height')}</FormLabel>
                    <FormControl>
                      <IconInput
                        type="number"
                        inputMode="numeric"
                        min={120}
                        max={230}
                        trailing={<span className="pe-1 text-xs">cm</span>}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="clubId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.club')}</FormLabel>
                    <FormControl>
                      <ClubSelect value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormDescription>{t('players.form.clubHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="preferredFoot"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.preferredFoot')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NONE}>
                          {t('players.form.preferredFootPlaceholder')}
                        </SelectItem>
                        {Object.values(CreatePlayerDtoPreferredFoot).map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`players.foot.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="marketValue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.marketValue')}</FormLabel>
                    <FormControl>
                      <IconInput type="number" min={0} step={100000} icon="€" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="weeklyWage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.weeklyWage')}</FormLabel>
                    <FormControl>
                      <IconInput type="number" min={0} step={1000} icon="€" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contractUntil"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('players.form.contractUntil')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription>{t('players.form.contractUntilHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="representation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('players.form.representation')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PLAYER_REPRESENTATIONS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`players.representation.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {banner ? <Callout variant="destructive" title={banner} /> : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" loading={createPlayer.isPending}>
                {t('players.add.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
